// src/components/dashboard/rutinas/RoutineTabs.js
import React, { useState, useMemo, useEffect, forwardRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  Divider,
  useTheme,
  TabScrollButton, // ← importamos el botón original
  alpha,
} from "@mui/material";
import LightbulbIcon from "@mui/icons-material/LightbulbOutlined";

/* ---------- scroll-button sin Fade ---------- */
const SolidScrollButton = forwardRef(function SolidScrollButton(props, ref) {
  return (
    <TabScrollButton
      {...props}
      ref={ref}
      sx={{ opacity: props.disabled ? 0 : 1, transition: "none" }}
    />
  );
});

/* ---------- utilidades ---------- */
const dayNames = [
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
  "domingo",
];

const a11yProps = (index, prefix) => ({
  id: `${prefix}-tab-${index}`,
  "aria-controls": `${prefix}-tabpanel-${index}`,
});

const formatDateLong = (iso, withYear = false) => {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("es-MX", { month: "long" });
  return withYear
    ? `${day} de ${month} de ${d.getFullYear()}`
    : `${day} de ${month}`;
};

const normalize = (str) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/* ---------- componente ---------- */
export default function RoutineTabs({ rutinasAsignadas = [], color3 }) {
  const theme = useTheme();
  const total = rutinasAsignadas.length;

  /* ------------ rutina vigente (última) -------------- */
  const currentRoutine = total ? rutinasAsignadas[total - 1] : null;

  /* -------------- estado sección vigente ------------- */
  const [dayIdxCurrent, setDayIdxCurrent] = useState(0);

  /* -------------- estado historial ------------------- */
  const [showHistory, setShowHistory] = useState(false);

  /* rutinas antiguas: penúltima a la izquierda → más antigua a la derecha */
  const historyRoutines = useMemo(
    () => rutinasAsignadas.slice(0, -1).reverse(),
    [rutinasAsignadas]
  );

  const [historyIdx, setHistoryIdx] = useState(0);
  const [historyDayIdx, setHistoryDayIdx] = useState(0);

  useEffect(() => setHistoryDayIdx(0), [historyIdx]);
  useEffect(() => {
    if (historyRoutines.length === 0) setShowHistory(false);
    if (historyIdx >= historyRoutines.length) setHistoryIdx(0);
  }, [historyRoutines.length, historyIdx]);

  const historyRoutine = historyRoutines[historyIdx] || null;

  const routineRecommendations = useMemo(() => {
    const raw =
      currentRoutine?.recomendacionesParaRutina ||
      currentRoutine?.recomendaciones;
    if (!raw) return [];
    const entries = Array.isArray(raw) ? raw : Object.values(raw || {});
    return entries
      .map((rec, idx) => {
        if (!rec) return null;
        if (typeof rec === "string") {
          return {
            id: String(idx),
            titulo: `Recomendación ${idx + 1}`,
            descripcion: rec,
          };
        }
        const titulo = rec.titulo || rec.title || `Recomendación ${idx + 1}`;
        const descripcion =
          rec.descripcion || rec.description || rec.texto || rec.body || "";
        return {
          id: rec._id || rec.id || String(idx),
          titulo,
          descripcion,
        };
      })
      .filter(Boolean);
  }, [currentRoutine]);
  const hasRoutineRecommendations = routineRecommendations.length > 0;

  /* -------------- gradiente principal -------------- */
  const primaryGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${color3 || theme.palette.primary.main} 0%, ${
        color3 || theme.palette.primary.dark
      } 100%)`,
    [color3, color3, theme]
  );

  /* -------------- ejercicios rutina vigente -------- */
  const normalizedDayCurrent = useMemo(
    () => normalize(dayNames[dayIdxCurrent]),
    [dayIdxCurrent]
  );
  const currentExercises = useMemo(() => {
    if (!currentRoutine) return [];
    return currentRoutine.ejercicios.filter(({ dias = {} }) =>
      Object.entries(dias).some(
        ([k, v]) => v && normalize(k) === normalizedDayCurrent
      )
    );
  }, [currentRoutine, normalizedDayCurrent]);

  /* -------------- ejercicios rutina histórica ------ */
  const normalizedDayHistory = useMemo(
    () => normalize(dayNames[historyDayIdx]),
    [historyDayIdx]
  );
  const historyExercises = useMemo(() => {
    if (!historyRoutine) return [];
    return historyRoutine.ejercicios.filter(({ dias = {} }) =>
      Object.entries(dias).some(
        ([k, v]) => v && normalize(k) === normalizedDayHistory
      )
    );
  }, [historyRoutine, normalizedDayHistory]);

  const getKey = (ex, idx) =>
    ex.idEjercicioAgregado !== undefined
      ? ex.idEjercicioAgregado
      : `${ex._id}-${idx}`;

  if (!total) {
    return (
      <Box
        sx={{
          mt: 1,
          textAlign: "center",
          p: 4,
          bgcolor: "#fff",
          borderRadius: 3,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Hola, aún no tienes una rutina asignada.
        </Typography>
        <Typography>Comunícate con tu asesor para obtener una.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ---------- sección: rutina vigente ---------- */}
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          background: primaryGradient,
          backgroundClip: "text",
          color: "transparent",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        Mis Rutinas
      </Typography>

      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Rutina: {currentRoutine.tituloRutina}
        </Typography>
        <Typography variant="subtitle2">
          del {formatDateLong(currentRoutine.fechaInicio)} al{" "}
          {formatDateLong(currentRoutine.fechaFin, true)}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(135deg,#f8fafc,#fff)",
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          mb={hasRoutineRecommendations ? 1.5 : 0}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: alpha(color3 || theme.palette.primary.main, 0.12),
                display: "grid",
                placeItems: "center",
                color: color3 || theme.palette.primary.main,
              }}
            >
              <LightbulbIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Recomendaciones para tu rutina
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tips y recordatorios personalizados
              </Typography>
            </Box>
          </Stack>
          {hasRoutineRecommendations && (
            <Chip
              label={`${routineRecommendations.length} ${
                routineRecommendations.length === 1 ? "nota" : "notas"
              }`}
              sx={{
                fontWeight: 600,
                background: alpha(color3 || theme.palette.primary.main, 0.15),
              }}
            />
          )}
        </Stack>

        {hasRoutineRecommendations ? (
          <Stack spacing={1.25}>
            {routineRecommendations.map((rec) => (
              <Box
                key={rec.id}
                sx={{
                  borderRadius: 2,
                  border: "1px solid rgba(15,23,42,0.08)",
                  background: "#fff",
                  p: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  {rec.titulo}
                </Typography>
                {rec.descripcion && (
                  <Typography variant="body2" color="text.secondary">
                    {rec.descripcion}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Tu nutriólogo aún no agrega recomendaciones para esta rutina.
          </Typography>
        )}
      </Box>

      {/* pestañas de día para RUTINA ACTUAL */}
      <Tabs
        value={dayIdxCurrent}
        onChange={(_, v) => setDayIdxCurrent(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        ScrollButtonComponent={SolidScrollButton}
        TabIndicatorProps={{ style: { backgroundColor: color3 } }}
        sx={{
          mb: 3,
          "& .MuiTab-root.Mui-selected": {
            color: color3,
          },
        }}
      >
        {dayNames.map((d, idx) => (
          <Tab
            key={d}
            label={d.charAt(0).toUpperCase() + d.slice(1)}
            {...a11yProps(idx, "dayCurr")}
          />
        ))}
      </Tabs>

      {currentExercises.length === 0 ? (
        <Typography align="center">Sin ejercicios para este día.</Typography>
      ) : (
        <Grid container spacing={3}>
          {currentExercises.map((ex, idx) => (
            <Grid item xs={12} sm={6} md={4} key={getKey(ex, idx)}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1,
                      background: primaryGradient,
                      backgroundClip: "text",
                      color: "transparent",
                      fontWeight: 700,
                    }}
                  >
                    {ex.nombre}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1, flexWrap: "wrap" }}
                  >
                    <Chip label={`Principal: ${ex.musculoPrincipal}`} />
                    {ex.musculosSecundarios.map((m) => (
                      <Chip key={m} label={m} variant="outlined" />
                    ))}
                  </Stack>

                  <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                    Series: <b>{ex.serie || "—"}</b>
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                    Repeticiones: <b>{ex.repeticiones || "—"}</b>
                  </Typography>
                  {(ex.horas || ex.minutos) && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      Duración:{" "}
                      <b>
                        {ex.horas ? `${ex.horas}h ` : ""}
                        {ex.minutos ? `${ex.minutos}m` : ""}
                      </b>
                    </Typography>
                  )}
                  {ex.peso && (
                    <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                      Peso: <b>{ex.peso}</b> {ex.medida || ""}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                    Intensidad: <b>{ex.intensidad}</b>
                  </Typography>
                  {ex.descripcion && (
                    <Typography sx={{ fontSize: "0.85rem", mt: 1 }}>
                      Detalles:{ex.descripcion}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ---------- sección: historial ---------- */}
      {historyRoutines.length > 0 && (
        <>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => setShowHistory((p) => !p)}
              sx={{
                textTransform: "none",
                borderColor: color3,
                color: color3,
                "&:hover": {
                  backgroundColor: alpha(color3, 0.1),
                  borderColor: color3,
                },
              }}
            >
              {showHistory ? "Ocultar historial" : "Ver historial"}
            </Button>
          </Box>

          {showHistory && (
            <>
              <Divider sx={{ my: 3 }} />

              {/* pestañas de RUTINA histórica */}
              <Tabs
                value={historyIdx}
                onChange={(_, v) => setHistoryIdx(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                ScrollButtonComponent={SolidScrollButton}
                TabIndicatorProps={{ style: { backgroundColor: color3 } }}
                sx={{
                  mb: 2,
                  "& .MuiTab-root.Mui-selected": {
                    color: color3,
                  },
                }}
              >
                {historyRoutines.map((r, idx) => (
                  <Tab
                    key={r._id}
                    label={`${formatDateLong(r.fechaInicio)} → ${formatDateLong(
                      r.fechaFin,
                      true
                    )}`}
                    {...a11yProps(idx, "histR")}
                  />
                ))}
              </Tabs>

              {historyRoutine && (
                <>
                  {/* pestañas de día históricas */}
                  <Tabs
                    value={historyDayIdx}
                    onChange={(_, v) => setHistoryDayIdx(v)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                    ScrollButtonComponent={SolidScrollButton}
                    TabIndicatorProps={{ style: { backgroundColor: color3 } }}
                    sx={{
                      mb: 2,
                      "& .MuiTab-root.Mui-selected": {
                        color: color3,
                      },
                    }}
                  >
                    {dayNames.map((d, idx) => (
                      <Tab
                        key={d}
                        label={d.charAt(0).toUpperCase() + d.slice(1)}
                        {...a11yProps(idx, "histDay")}
                      />
                    ))}
                  </Tabs>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Rutina: {historyRoutine.tituloRutina}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    del {formatDateLong(historyRoutine.fechaInicio)} al{" "}
                    {formatDateLong(historyRoutine.fechaFin, true)}
                  </Typography>

                  {historyExercises.length === 0 ? (
                    <Typography align="center" sx={{ mb: 3 }}>
                      Sin ejercicios para este día.
                    </Typography>
                  ) : (
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      {historyExercises.map((ex, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={getKey(ex, idx)}>
                          <Card
                            sx={{
                              height: "100%",
                              borderRadius: 3,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              background: "#fafafa",
                            }}
                          >
                            <CardContent>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 1,
                                  background: primaryGradient,
                                  backgroundClip: "text",
                                  color: "transparent",
                                  fontWeight: 700,
                                }}
                              >
                                {ex.nombre}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mb: 1, flexWrap: "wrap" }}
                              >
                                <Chip
                                  label={`Principal: ${ex.musculoPrincipal}`}
                                />
                                {ex.musculosSecundarios.map((m) => (
                                  <Chip key={m} label={m} variant="outlined" />
                                ))}
                              </Stack>

                              <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                                Series: <b>{ex.serie || "—"}</b>
                              </Typography>
                              <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                                Repeticiones: <b>{ex.repeticiones || "—"}</b>
                              </Typography>
                              {(ex.horas || ex.minutos) && (
                                <Typography
                                  sx={{ fontSize: "0.9rem", mb: 0.5 }}
                                >
                                  Duración:{" "}
                                  <b>
                                    {ex.horas ? `${ex.horas}h ` : ""}
                                    {ex.minutos ? `${ex.minutos}m` : ""}
                                  </b>
                                </Typography>
                              )}
                              {ex.peso && (
                                <Typography
                                  sx={{ fontSize: "0.9rem", mb: 0.5 }}
                                >
                                  Peso: <b>{ex.peso}</b> {ex.medida || ""}
                                </Typography>
                              )}
                              <Typography sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                                Intensidad: <b>{ex.intensidad}</b>
                              </Typography>
                              {ex.descripcion && (
                                <Typography sx={{ fontSize: "0.85rem", mt: 1 }}>
                                  {ex.descripcion}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
